
import moment from 'moment';
import {
  defineComponent,
  ref,
  watch,
  onUnmounted,
  onMounted,
  reactive,
  inject,
} from 'vue';
import { Progress, } from 'ant-design-vue';
import * as echarts from 'echarts';
import {
  setFeatureThresholdInfo,
  getAqiSuggestion,
  windDirection,
} from '@/utils/thresholdInfo';

import { postRequest, } from '@/request/request';

export default defineComponent({
  components: {
    Progress,
  },
  props: {
    theme: {
      type: String,
      default: '',
      requir: true,
    },
    popupData: {
      type: Object,
      default: () => ({}),
      requir: true,
    },
  },
  setup (props, { emit, }) {
    interface Pop {
      [propName: string]: any;
    }
    const popupRef = ref<any | null>(null);

    const historyData = ref<Pop>({});
    const weatherVo = ref<Pop>({});

    // 高值溯源按钮状态
    const exceedActive = ref(false);
    const activeFromMain :any = inject('exceed');

    // const indexName = computed(() => store.state.layerConsole.indexName);
    const indexName = ref<string>('');
    const pollute = [
      {
        name: 'PM₂.₅',
        key: 'pm25',
        unit: 'μg/m³',
        decimal: 0,
      },
      {
        name: 'PM₁₀',
        key: 'pm10',
        unit: 'μg/m³',
        decimal: 0,
      },
      {
        name: 'SO₂',
        key: 'so2',
        unit: 'μg/m³',
        decimal: 0,
      },
      {
        name: 'NO₂',
        key: 'no2',
        unit: 'μg/m³',
        decimal: 0,
      },
      {
        name: 'O₃',
        key: 'o3',
        unit: 'μg/m³',
        decimal: 0,
      },
      {
        name: 'CO',
        key: 'co',
        unit: 'mg/m³',
        decimal: 2,
      },
    ];

    const weather = [{
      name: '温度',
      key: 'tem',
      unit: '℃',
      decimal: 0,
    },
    {
      name: '风速',
      key: 'windS',
      unit: 'm/s',
      decimal: 2,
    },
    {
      name: '湿度',
      key: 'rhu',
      unit: '%',
      decimal: 0,
    },
    {
      name: '气压',
      key: 'prs',
      unit: 'hPa',
      decimal: 0,
    },
    {
      name: '风向',
      key: 'windD',
      unit: '',
      decimal: 0,
    },
    {
      name: '降水',
      key: 'pre',
      unit: 'mm',
      decimal: 0,
    },];

    // 要素对应文字
    const factorKey = {
      PM25: 'PM₂.₅',
      PM10: 'PM₁₀',
      SO2: 'SO₂',
      NO2: 'NO₂',
      CO: 'CO',
      O3: 'O₃',
      AQI: 'AQI',
    };

    // 弹窗数据，父组件传递
    const popInfos = reactive({
      theme: '',
    });

    const changeRankingVisable = () => {
      emit('close');
    };

    const flag = ref<boolean>(false);

    const chart = ref<any>({});
    const firstPollutionText = ref<string>('');
    const dataPop = ref<any>({});

    /**
     *  配置柱状图
     * @param {any} val 配置项
     * @return {void}
     */
    const setChart = (val: any) => {
      setTimeout(() => {
        if (val && Object.keys(chart.value).length) {
          chart.value.setOption(val);
        }
      }, 1000);
    };

    /**
     * 初始化图表
     * @returns {void}
     */
    const initCharts = () => {
      const container = document.getElementById('chart-container') as HTMLElement;

      chart.value = echarts.init(container);
    };

    /**
     * 返回单位
     * @returns {string} 单位
     */
    const unit = () => {
      if (indexName.value === 'AQI') {
        return '';
      }

      if (indexName.value === 'CO') {
        return 'mg/m³';
      }

      return 'μg/m³';
    };

    const validVal = (item: any, data: number) => {
      const { key, decimal, } = item;

      if (key === 'windD') {
        return data[key] && data[key] !== '-9999' && data[key] !== '99999' ?
          windDirection(Number(data[key])) :
          '-';
      }

      return data[key] && data[key] !== '-9999' && data[key] !== '99999' ?
        Number(data[key]).toFixed(decimal) :
        '-';
    };

    const polluteInfo = (item: any, data: any) => `${validVal(item, data)}${item.unit}`;

    const factorColor = (item: any, opacity: string) => `${setFeatureThresholdInfo(Number(props.popupData[item.key]), item.key)?.color}${opacity}`;

    const weatherInfo = (item: any, data: any) => `${item.name}: ${validVal(item, data)}${item.unit}`;

    const getPercent = (val: any) => props.popupData[val.key] / 2.5;

    // 请求站点历史数据
    const queryPmData = (param: any) => new Promise((resolve) => {
      const url = param.stationType === 4 ?
        `/airMap/queryStationHistory` :
        `/gaochun/airMap/queryStationHistory`;

      postRequest(url, param).then((res:any) => {
        resolve(res.data);
      });
    });

    const getData = async (params: any) => {
      const name = indexName.value?.toLowerCase();
      const temp: any = [];
      const res: any = await queryPmData(params);

      if (res) {
        historyData.value = res.airHistory || {};
        weatherVo.value = res.weatherVo || {};

        // eslint-disable-next-line multiline-ternary
        firstPollutionText.value = historyData.value?.[0]?.firstPollution ? factorKey[historyData.value[0].firstPollution] : '-';
        historyData.value.forEach((element: any) => {
          if (Number(element[name]) >= 0) {
            temp.push(element);
          }
        });
      }
      //  柱状图配置项
      const option = {
        grid: {
          // x: '',
          y: '10px',
          // 高度
          height: '60%',
          width: '85%',
        },
        xAxis: {
          type: 'category',
          data: temp.map((item: any) => {
            const hour = moment(item.reportTime, 'YYYYMMDDHH').format('HH');

            if (hour === '00') {
              return `${hour},${moment(item.reportTime, 'YYYYMMDDHH').format('MM月DD日')}`;
            }

            return hour;
          }),
          // 隐藏刻度线
          axisTick: {
            show: false,
          },
          axisLabel: {
            show: true,
            // 显示全部
            interval: 0,
            textStyle: {
              color: '#888888',
              fontSize: '14',
            },
            formatter: (value: any) => {
              if (value.length > 2) {
                return value.replace(',', '\n');
              }
              if (Number(value) % 8 === 0) {
                return value;
              }

              return null;
            },
            align: 'left',
          },
          axisPointer: {
            show: true,
            type: 'shadow',
            label: {
              show: false,
            },
          },
        },
        yAxis: {
          type: 'value',
          // 隐藏y轴
          axisLine: {
            show: false,
          },
          // 隐藏刻度线
          axisTick: {
            show: false,
          },

          //  网格线设置
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#B3B3B3',
              opacity: '0.4',
            },
          },
          axisLabel: {
            interval: 6,
            show: true,
            textStyle: {
              fontSize: '14',
              color: '#888888',
            },
          },
        },

        series: [
          {
            type: 'bar',
            barMaxWidth: 30,
            data: temp.map((item: any) => ({
              value: item[name],
              itemStyle: {
                normal: {
                  borderRadius: [10,
                    10,
                    0,
                    0,],
                  color: setFeatureThresholdInfo(item[name], name)?.color,
                },
              },
            })),
          },
        ],
        tooltip: {
          show: true,
          formatter: (param: any) => {
            const tempName = `${param.name.split('').splice(0, 2).join('')}时`;

            return `<div style=" ">${param.marker}<span style="font-weight:bold;">${tempName
            }</span> ${param.value}${unit()}</div>`;
          },
          textStyle: {
            align: 'left',
            fontSize: '16',
          },
        },
      };

      setChart(option);
    };

    watch(activeFromMain, (val:boolean) => {
      exceedActive.value = val;
    },{
      immediate: true,
    });
    // 高值溯源按钮点击事件
    const exceedClick = () => {
      exceedActive.value = !exceedActive.value;
      emit('exceedChange', exceedActive.value);
    };

    watch(() => props.popupData, (cur:any, old:any) => {
      dataPop.value = cur;
      const { infoWinType,id, isWs, } = dataPop.value;

      // ws数据，只变化基础数据
      if (isWs) return;

      // 无基础数据，不请求
      if (!id) {
        historyData.value = {};
        weatherVo.value = {};

        return;
      }

      indexName.value = dataPop.value.showType ?? indexName.value;

      let stationType = null;

      if (infoWinType === 'country') {
        stationType = 4;
      } else if (infoWinType === 'province') {
        stationType = 1;
      } else if (infoWinType === 'city') {
        stationType = 2;
      } else if (infoWinType === 'micro') {
        stationType = 3;
      } else if (infoWinType === 'moveTrack') {
        stationType = 5;
      }

      const params = {
        stationType,
        pointGid: id,
        endTime: moment(dataPop.value.currentTime, 'YYYYMMDDHH').format('YYYYMMDDHH'),
        startTime: moment(dataPop.value.currentTime, 'YYYYMMDDHH').subtract(2, 'day').format('YYYYMMDD00'),
      };

      getData(params);
    },{ immediate: true, });

    /**
     *  重置图表
     * @param {any} val value
     * @return {void}
     */
    const reSize = (): any => {
      if (Object.keys(chart.value).length) {
        chart.value.resize();
      }
    };

    const setStyleTheme = (mapStyle: string) => {
      popInfos.theme = mapStyle;
    };

    /**
     * @todo 调用resize方法和拉动滚动轴
     */
    onMounted(() => {
      flag.value = true;
      initCharts();
      window.addEventListener('resize', reSize);
    });

    /**
     * 销毁图表
     */
    onUnmounted(() => {
      window.removeEventListener('resize', reSize);

      echarts.dispose(chart);
      chart.value = {};
      flag.value = false;
    });

    return {
      props,
      dataPop ,
      historyData,
      weatherVo,
      moment,
      indexName,
      pollute,
      weather,
      popupRef,
      factorKey,
      exceedActive,
      changeRankingVisable,
      factorColor,
      getPercent,
      polluteInfo,
      weatherInfo,
      exceedClick,
      firstPollutionText,
    };
  },
  computed: {
    thresholdInfo () {
      return setFeatureThresholdInfo(Number(this.dataPop.aqi), 'aqi');
    },
    aqiFontColor () {
      return { color: this.thresholdInfo.fontColor, };
    },
    getAqiValue () {
      return !this.dataPop.aqi || this.dataPop.aqi === '-9999' || this.dataPop.aqi === null ?
        '-' :
        Number(this.dataPop.aqi);
    },
    titleTime () {
      const { currentTime, } = this.props.popupData;

      return currentTime ?
        moment(currentTime, 'YYYYMMDDHHmm').format('YY-MM-DD HH:mm:ss') :
        moment(this.dataPop.reportTime, 'YYYYMMDDHHmm').format('YY-MM-DD HH:mm:ss');
    },
    suggestionText () {
      return this.dataPop.suggestion || getAqiSuggestion(Number(this.dataPop.aqi));
    },
    firstPollutionColor () {
      const fontColor = this.thresholdInfo.color === '#fefe55' ?
        'rgb(68, 67, 0)' :
        this.thresholdInfo.color;

      return { color: fontColor, };
    },
  },
});
